import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Cloud, Workflow, Code, Container, Layers, Shield, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { SEO } from '../components/seo/SEO';
import content from '../content.json';

const iconMap = {
  Cloud,
  Workflow,
  Code,
  Container,
  Layers,
  Shield,
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
    },
  },
};

export default function Services() {
  const { title, subtitle, items } = content.services;

  return (
    <>
      <SEO 
        title={content.seo.services.title}
        description={content.seo.services.description}
        canonical="https://dakshtechinfo.uk/services"
      />
      
      <div data-testid="services-page">
        {/* Hero Section */}
        <section className="section-padding bg-muted/30" data-testid="services-hero">
          <div className="container-custom">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="max-w-3xl mx-auto text-center"
            >
              <motion.h1 
                variants={itemVariants}
                className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6"
              >
                {title}
              </motion.h1>
              <motion.p 
                variants={itemVariants}
                className="text-base md:text-lg text-muted-foreground"
              >
                {subtitle}
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* Services Grid - Bento Style */}
        <section className="section-padding" data-testid="services-grid">
          <div className="container-custom">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6"
            >
              {items.map((service, index) => {
                const Icon = iconMap[service.icon] || Cloud;
                // Bento grid sizing
                const gridClasses = [
                  'lg:col-span-8',
                  'lg:col-span-4',
                  'lg:col-span-4',
                  'lg:col-span-8',
                  'lg:col-span-6',
                  'lg:col-span-6',
                ][index] || 'lg:col-span-6';

                return (
                  <motion.div 
                    key={service.id} 
                    variants={itemVariants}
                    className={gridClasses}
                  >
                    <Card 
                      className="h-full hover-lift border-border/50 bg-card group"
                      data-testid={`service-${service.id}`}
                    >
                      <CardContent className="p-6 md:p-8 h-full flex flex-col">
                        <div className="flex items-start justify-between mb-6">
                          <div className="w-14 h-14 rounded-xl bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                            <Icon className="h-7 w-7 text-accent" />
                          </div>
                          <span className="text-xs font-medium text-muted-foreground bg-muted px-3 py-1 rounded-full">
                            {service.subtitle}
                          </span>
                        </div>
                        
                        <h3 className="text-xl md:text-2xl font-bold mb-3">
                          {service.title}
                        </h3>
                        
                        <p className="text-muted-foreground mb-6 flex-grow">
                          {service.description}
                        </p>
                        
                        <div className="space-y-3">
                          <p className="text-sm font-medium text-foreground">Key Outcomes:</p>
                          <ul className="space-y-2">
                            {service.outcomes.map((outcome, idx) => (
                              <li 
                                key={idx}
                                className="flex items-center gap-2 text-sm text-muted-foreground"
                              >
                                <CheckCircle2 className="h-4 w-4 text-accent flex-shrink-0" />
                                {outcome}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section-padding bg-muted/30" data-testid="services-cta">
          <div className="container-custom">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="text-center max-w-2xl mx-auto"
            >
              <motion.h2 
                variants={itemVariants}
                className="text-2xl md:text-3xl font-bold mb-4"
              >
                Not Sure Where to Start?
              </motion.h2>
              <motion.p 
                variants={itemVariants}
                className="text-muted-foreground mb-8"
              >
                Book a free consultation and we'll help you identify the right solutions for your business needs.
              </motion.p>
              <motion.div variants={itemVariants}>
                <Button 
                  size="lg" 
                  asChild
                  className="group"
                  data-testid="services-contact-cta"
                >
                  <Link to="/contact">
                    Schedule a Consultation
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}
